import { useCallback, useEffect, useRef, useState } from 'react';

import { apiClient } from '../services/apiClient';
import {
  knowledgeService,
  type KnowledgeParticleOverlayExtractView,
} from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';

const POLL_INTERVAL_MS = 15_000;
const MAX_POLL_ATTEMPTS = 12;

function asMetaRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {};
}

function overlayUnsettled(status: string | null | undefined): boolean {
  const value = String(status || '').trim().toLowerCase();
  return value === 'running' || value === 'pending';
}

function mergeOverlayExtractIntoParticle(
  particle: KnowledgeParticle,
  view: KnowledgeParticleOverlayExtractView,
  layers: 'coach' | 'learner' | 'both',
): KnowledgeParticle {
  const nextMeta = asMetaRecord(particle.metadata);
  if (layers === 'coach' || layers === 'both') {
    nextMeta.coachOverlay = view.coachOverlay;
    nextMeta.coachOverlayStatus = view.coachOverlayStatus;
    nextMeta.coachOverlayError = view.coachOverlayError;
  }
  if (layers === 'learner' || layers === 'both') {
    nextMeta.learnerEvidence = view.learnerEvidence;
    nextMeta.learnerEvidenceStatus = view.learnerEvidenceStatus;
    nextMeta.learnerEvidenceError = view.learnerEvidenceError;
  }
  return { ...particle, metadata: nextMeta };
}

export function useKnowledgeParticleOverlays(input: {
  particleId?: string | null;
  particle?: KnowledgeParticle | null;
  enabled?: boolean;
  autoKickoff?: boolean;
  onParticleUpdated?: (particle: KnowledgeParticle) => void | Promise<void>;
}) {
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollAttemptsRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const particleRef = useRef(input.particle);
  const onParticleUpdatedRef = useRef(input.onParticleUpdated);
  particleRef.current = input.particle;
  onParticleUpdatedRef.current = input.onParticleUpdated;

  const applyView = useCallback(async (
    view: KnowledgeParticleOverlayExtractView,
    layers: 'coach' | 'learner' | 'both',
  ) => {
    const particle = particleRef.current;
    const onParticleUpdated = onParticleUpdatedRef.current;
    if (particle && onParticleUpdated) {
      await onParticleUpdated(mergeOverlayExtractIntoParticle(particle, view, layers));
    }
  }, []);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollOverlays = useCallback(async (kickoff: boolean) => {
    if (!input.enabled || !input.particleId) return null;
    const userId = apiClient.getCurrentUser()?.id;
    if (!userId) {
      setError('未登录');
      setExtracting(false);
      return null;
    }

    try {
      const view = await knowledgeService.getKnowledgeParticleOverlays(input.particleId, {
        userId,
        kickoff,
      });
      if (cancelledRef.current) return view;
      await applyView(view, 'both');
      const unsettled = overlayUnsettled(view.coachOverlayStatus)
        || overlayUnsettled(view.learnerEvidenceStatus);
      setExtracting(unsettled);
      setError(view.coachOverlayError || view.learnerEvidenceError || null);
      if (unsettled && pollAttemptsRef.current < MAX_POLL_ATTEMPTS) {
        pollAttemptsRef.current += 1;
        clearPollTimer();
        pollTimerRef.current = setTimeout(() => {
          void pollOverlays(false);
        }, POLL_INTERVAL_MS);
      }
      return view;
    } catch (loadError) {
      if (!cancelledRef.current) {
        console.warn('[useKnowledgeParticleOverlays] poll failed', loadError);
      }
      return null;
    }
  }, [applyView, clearPollTimer, input.enabled, input.particleId]);

  const extractOverlays = useCallback(async (
    layers: 'coach' | 'learner' | 'both' = 'both',
    options?: { force?: boolean; wait?: boolean },
  ) => {
    if (!input.enabled || !input.particleId) return null;
    const userId = apiClient.getCurrentUser()?.id;
    if (!userId) {
      setError('未登录');
      return null;
    }

    const wait = options?.wait !== false;
    setExtracting(true);
    setError(null);
    try {
      const view = await knowledgeService.extractKnowledgeParticleOverlays(input.particleId, {
        userId,
        force: options?.force !== false,
        wait,
        layers,
      });
      await applyView(view, layers);
      const unsettled = overlayUnsettled(view.coachOverlayStatus)
        || overlayUnsettled(view.learnerEvidenceStatus);
      if (!wait && unsettled) {
        pollAttemptsRef.current = 0;
        pollTimerRef.current = setTimeout(() => {
          void pollOverlays(false);
        }, POLL_INTERVAL_MS);
        setExtracting(true);
        return view;
      }
      setExtracting(unsettled);
      return view;
    } catch (extractError) {
      const message = extractError instanceof Error ? extractError.message : '提取教练/学情层失败';
      setError(message);
      setExtracting(false);
      return null;
    }
  }, [applyView, input.enabled, input.particleId, pollOverlays]);

  useEffect(() => {
    cancelledRef.current = false;
    pollAttemptsRef.current = 0;
    if (!input.autoKickoff || !input.enabled || !input.particleId) {
      return undefined;
    }
    setExtracting(true);
    void pollOverlays(true);
    return () => {
      cancelledRef.current = true;
      clearPollTimer();
    };
  }, [clearPollTimer, input.autoKickoff, input.enabled, input.particleId, pollOverlays]);

  return {
    extracting,
    error,
    extractOverlays,
  };
}
